import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { LocalizationState } from '@nimbus-library/features/localization/store/localization.state';
import { AuthorType, IChatMessage } from '@nimbus-global/features/chat/models/chat.model';
import { ChatFormFieldType, ChatFormOperation, IChatForm, IChatFormField } from '@nimbus-global/features/chat-form/models/chat-form.model';
import {
  CompanionConfirmationModel,
  CompanionClientHints,
  CompanionConfirmationResult,
  CompanionConversationMessageModel,
  CompanionConversationModel,
  CompanionDocumentSourceModel,
  CompanionForm,
  CompanionFormField,
  CompanionFormFieldType,
  CompanionFormModel,
  CompanionFormOperation,
  CompanionInternalStatus,
  CompanionMessageError,
  CompanionMessageType,
  CompanionReferenceType,
  CompanionReferenceTypesCascade,
  CompanionToolCallRecord,
  CompanionToolErrorCode,
  CompanionToolName,
  ConversationMessageModel,
} from '@nimbus-library/features/companion-chat/models/companion-conversation.model';
import { IEnvironmentService } from '@nimbus-library/features/configuration/i-environment.service';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { CompanionChatMessage } from '@nimbus-library/features/companion-chat/models/companion-chat-message.model';
import { CompanionChatChartService } from './companion-chat-chart.service';
import { CompanionChatReferenceService } from './companion-chat-reference.service';
import { CompanionChatResolver } from './companion-chat-resolver';

@Injectable({ providedIn: 'root' })
export class CompanionChatService {
  private readonly maxAuthorInfoSources = 3;
  private readonly httpErrorNotFound = 404;
  private readonly httpErrorRateLimited = 429;
  private readonly httpErrorUnprocessable = 422;
  private readonly referenceTypeMapping: Readonly<Record<string, CompanionReferenceType>> = {
    [CompanionToolName.CreateVirtualUser]: CompanionReferenceType.VirtualUser,
    [CompanionToolName.UpdateVirtualUser]: CompanionReferenceType.VirtualUser,
  };

  public constructor(
    private readonly store: Store,
    private readonly translateService: TranslateService,
    private readonly environmentService: IEnvironmentService,
    private readonly chatChartService: CompanionChatChartService,
    private readonly chatReferenceService: CompanionChatReferenceService,
    private readonly companionChatResolver: CompanionChatResolver,
  ) {}

  public formatDate(createdAt: string, includeTime = true): string {
    const createdAtMoment = moment(createdAt);

    if (!createdAtMoment.isValid()) {
      return '';
    }

    const lang = this.store.selectSnapshot(LocalizationState.selectedDateFormat);
    const localized = createdAtMoment.locale(lang);

    if (createdAtMoment.isSame(moment(), 'day')) {
      return includeTime
        ? this.translateService.instant(TranslationKeys.GeneralTodayWithTime, { value: localized.format('LT') })
        : this.translateService.instant(TranslationKeys.GeneralToday);
    }

    if (createdAtMoment.isSame(moment().subtract(1, 'days'), 'day')) {
      return includeTime
        ? this.translateService.instant(TranslationKeys.GeneralYesterdayWithTime, { value: localized.format('LT') })
        : this.translateService.instant(TranslationKeys.GeneralYesterday);
    }

    return includeTime ? localized.format('L, LT') : localized.format('L');
  }

  public buildChatMessages<T extends IChatMessage>(messages: T[]): T[] {
    const sorted = [...messages].sort((a, b) => a.dateTime.localeCompare(b.dateTime));
    return sorted.map((msg, i) => this.extendWithFirstDay(msg, i, sorted));
  }

  public mapResponseToMessages(messages: ConversationMessageModel[], hasMoreMessages: boolean): CompanionChatMessage[] {
    const model = messages.map((msg, index) => this.mapToChatMessage(msg, index));
    return this.adjustResponseMessagesForm(model, hasMoreMessages);
  }

  public buildClientHints(route: ActivatedRoute): CompanionClientHints {
    return {
      routeTemplate: this.getRouteTemplate(route),
      referralSource: this.environmentService.environment.applicationType,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      dateFormat: this.getDateTimePattern(),
    };
  }

  public createAgentTypingMessage(index: number): IChatMessage {
    return {
      ...this.createMessage(
        `typing-${Date.now()}`,
        TranslationKeys.CompanionChatAuthorAgent,
        AuthorType.Companion,
        this.translateService.instant(TranslationKeys.CompanionChatTyping),
        index,
      ),
      isTypingContent: true,
    };
  }

  public getErrorKey(err: { status?: number; httpStatus?: number }): TranslationKeys {
    const status = err?.status ?? err?.httpStatus;
    if (status === this.httpErrorNotFound) {
      return TranslationKeys.CompanionChatNotFoundError;
    }
    if (status === this.httpErrorRateLimited) {
      return TranslationKeys.CompanionChatRateLimitedError;
    }
    if (status === this.httpErrorUnprocessable) {
      return TranslationKeys.CompanionChatUnprocessableError;
    }
    return TranslationKeys.CompanionChatFailSendMessage;
  }

  public getToolErrorKey(code: string): TranslationKeys {
    switch (code) {
      case CompanionToolErrorCode.LicenseLimitExceeded:
        return TranslationKeys.CompanionChatLicenseLimitExceededError;
      case CompanionToolErrorCode.Forbidden:
        return TranslationKeys.CompanionChatForbiddenError;
      case CompanionToolErrorCode.Unauthorized:
        return TranslationKeys.CompanionChatUnauthorizedError;
      case CompanionToolErrorCode.Unavailable:
        return TranslationKeys.CompanionChatUnavailableError;
      case CompanionToolErrorCode.Timeout:
        return TranslationKeys.CompanionChatTimeoutError;
      case CompanionToolErrorCode.InvalidInput:
        return TranslationKeys.CompanionChatUnprocessableError;
      default:
        return null;
    }
  }

  public createSystemErrorMessage(index: number, errorKey: TranslationKeys = TranslationKeys.CompanionChatFailSendMessage): IChatMessage {
    return {
      ...this.createMessage(
        `error-${Date.now()}-${index}`,
        TranslationKeys.CompanionChatAuthorSystem,
        AuthorType.System,
        this.translateService.instant(errorKey),
        index,
      ),
      contentIcon: ICONS.EXCLAMATION_TRIANGLE_LIGTH,
    };
  }

  public buildAgentMessage(
    index: number,
    content: string,
    toolCalls: CompanionToolCallRecord[] = [],
    messageType?: CompanionMessageType,
    requestId?: string,
    form?: CompanionForm,
    error?: CompanionMessageError,
  ): CompanionChatMessage {
    const errorKey = error ? this.getToolErrorKey(error.code) : null;
    const effectiveContent = errorKey ? this.translateService.instant(errorKey) : content;
    const sources = this.buildMessageSources(toolCalls, this.maxAuthorInfoSources);
    const base: CompanionChatMessage = {
      ...this.createMessage(
        `agent-${Date.now()}-${index}`,
        TranslationKeys.CompanionChatAuthorAgent,
        AuthorType.Companion,
        effectiveContent,
        index,
      ),
      showAuthorInfo: !!sources,
      isMarkdown: !error,
      sources,
      requestId,
      contentIcon: error ? ICONS.EXCLAMATION_TRIANGLE_LIGTH : undefined,
    };
    if (error) {
      return base;
    }
    const chart = this.chatChartService.build(toolCalls);
    const references = this.chatReferenceService.build(toolCalls);
    return {
      ...base,
      form: this.buildForm(messageType, form),
      chartType: chart?.type,
      chartData$: chart?.data$,
      referenceLinks: this.chatReferenceService.resolveLinks(references, this.companionChatResolver),
    };
  }

  public notifyEntitiesChanged(response: CompanionConversationModel): void {
    if (
      response?.internalStatus !== CompanionInternalStatus.Success ||
      !response.message ||
      response.message.error ||
      [CompanionMessageType.ConfirmationRequired, CompanionMessageType.Form].includes(response.message.messageType)
    ) {
      return;
    }
    for (const kind of this.chatReferenceService.getReferencesChanged(response.message.toolCalls, this.referenceTypeMapping)) {
      this.companionChatResolver.notifyEntityChanged?.(kind);
    }
  }

  public buildResponse(
    operation: ChatFormOperation,
    requestId: string,
    route: ActivatedRoute,
    values?: Record<string, unknown>,
    comment?: string,
    labels?: Record<string, string>,
  ): CompanionConversationMessageModel {
    const clientHints = this.buildClientHints(route);

    if (operation === ChatFormOperation.Confirm) {
      const decision = values ? CompanionConfirmationResult.Approved : CompanionConfirmationResult.Rejected;
      return {
        confirmationResponse: { requestId, decision, comment } as CompanionConfirmationModel,
        clientHints,
      };
    }

    return {
      formSubmission: { requestId, values: values as Record<string, string>, labels } as CompanionFormModel,
      clientHints,
    };
  }

  public createUserMessageByOperation(index: number, form: IChatForm, values?: Record<string, unknown>): CompanionChatMessage {
    if (form.operation === ChatFormOperation.Confirm) {
      const decision = values ? CompanionConfirmationResult.Approved : CompanionConfirmationResult.Rejected;
      return this.createUserMessageForConfirmation(index, decision);
    }
    return this.createUserMessageWithFormReadonly(index, form, values ?? {});
  }

  public createUserMessageWithFormReadonly(index: number, form: IChatForm, values: Record<string, unknown>): CompanionChatMessage {
    const base = this.createUserMessage(index, '');
    return { ...base, form: { ...form, values } };
  }

  public createUserMessageForConfirmation(index: number, decision: CompanionConfirmationResult): CompanionChatMessage {
    const isApproved = decision === CompanionConfirmationResult.Approved;
    const key = isApproved ? TranslationKeys.GeneralApproved : TranslationKeys.GeneralRejected;
    const base = this.createUserMessage(index, this.translateService.instant(key));
    return { ...base, contentIcon: isApproved ? ICONS.CHECK_CIRCLE : ICONS.TIMES_CIRCLE };
  }

  public createUserMessage(index: number, content: string): IChatMessage {
    return this.createMessage(`customer-${Date.now()}-${index}`, TranslationKeys.CompanionChatAuthorUser, AuthorType.Agent, content, index);
  }

  private adjustResponseMessagesForm(messages: CompanionChatMessage[], hasMoreMessages: boolean): CompanionChatMessage[] {
    return messages.map((m, i) => ((i > 0 || hasMoreMessages) && m.form && m.form.values === undefined ? { ...m, form: undefined } : m));
  }

  private mapHistoryUserMessage(msg: ConversationMessageModel, index: number): CompanionChatMessage | null {
    if (msg.messageType === CompanionMessageType.FormResolved) {
      const form = this.buildForm(CompanionMessageType.Form, msg.formSubmission?.form);
      if (form) {
        return this.createUserMessageWithFormReadonly(index, form, msg.formSubmission?.values);
      }
    }
    if (msg.messageType === CompanionMessageType.ConfirmationResolved && msg.confirmationSubmission) {
      return this.createUserMessageForConfirmation(index, msg.confirmationSubmission.decision);
    }
    return null;
  }

  private buildForm(messageType: CompanionMessageType, form: CompanionForm): IChatForm {
    switch (messageType) {
      case CompanionMessageType.ConfirmationRequired:
        return {
          entity: '',
          operation: ChatFormOperation.Confirm,
          fields: [],
        };
      case CompanionMessageType.Form:
        return this.mapForm(form);
      default:
        return undefined;
    }
  }

  private mapForm(form: CompanionForm): IChatForm {
    if (!form?.fields?.length) {
      return undefined;
    }
    const hasTenantField = form.fields.some((f) => f.reference === CompanionReferenceType.Tenant);
    return {
      entity: form.entity,
      operation: this.mapFormOperation(form.operation),
      fields: form.fields.flatMap((x) => this.mapFormField(x, hasTenantField)),
    };
  }

  private mapFormOperation(operation: CompanionFormOperation): ChatFormOperation {
    switch (operation) {
      case CompanionFormOperation.Update:
        return ChatFormOperation.Update;
      case CompanionFormOperation.Read:
        return ChatFormOperation.Read;
      case CompanionFormOperation.Create:
      default:
        return ChatFormOperation.Create;
    }
  }

  private mapFormField(field: CompanionFormField, hasTenantField: boolean): IChatFormField[] {
    if (field.type === CompanionFormFieldType.Reference && !hasTenantField) {
      switch (field.reference) {
        case CompanionReferenceType.Team:
        case CompanionReferenceType.OrganizationUnit:
          return this.expandWithTenantField(field);
      }
    }
    return [
      {
        name: field.name,
        description: field.description,
        type: this.mapFormFieldType(field.type),
        required: !!field.required,
        secret: !!field.secret,
        referenceType: field.reference,
        isCascade: CompanionReferenceTypesCascade.includes(field.reference),
        valueDefault: field.value,
      },
    ];
  }

  private expandWithTenantField(field: CompanionFormField): IChatFormField[] {
    return [
      {
        name: 'tenantId',
        type: ChatFormFieldType.Reference,
        required: !!field.required,
        secret: false,
        referenceType: CompanionReferenceType.Tenant,
        isCascade: true,
      },
      {
        name: field.name,
        description: field.description,
        type: ChatFormFieldType.Reference,
        required: !!field.required,
        secret: !!field.secret,
        referenceType: field.reference,
        isCascade: CompanionReferenceTypesCascade.includes(field.reference),
        valueDefault: field.value,
      },
    ];
  }

  private mapFormFieldType(type: CompanionFormFieldType): ChatFormFieldType {
    switch (type) {
      case CompanionFormFieldType.Reference:
        return ChatFormFieldType.Reference;
      case CompanionFormFieldType.DateTimeUtc:
        return ChatFormFieldType.DateTime;
      case CompanionFormFieldType.Text:
      default:
        return ChatFormFieldType.Text;
    }
  }

  private extendWithFirstDay<T extends IChatMessage>(msg: T, index: number, sorted: T[]): T {
    const isFirstOfTheDay = index === 0 || !moment(msg.dateTime).isSame(moment(sorted[index - 1].dateTime), 'day');
    return { ...msg, isFirstOfTheDay, firstOfTheDayLabel: isFirstOfTheDay ? this.formatDate(msg.dateTime, false) : undefined };
  }

  private buildMessageSources(toolCalls: CompanionToolCallRecord[], maxSources: number): CompanionDocumentSourceModel[] | null {
    const sources = this.extractDocumentSources(toolCalls);
    if (!sources.length) {
      return null;
    }
    return [...sources].sort((a, b) => b.score - a.score).slice(0, maxSources);
  }

  private extractDocumentSources(toolCalls: CompanionToolCallRecord[]): CompanionDocumentSourceModel[] {
    return toolCalls.flatMap((tc) => {
      const raw = tc.metadata?.['document_sources'];
      return Array.isArray(raw) ? (raw as CompanionDocumentSourceModel[]) : [];
    });
  }

  private createMessage(id: string, authorKey: TranslationKeys, authorType: AuthorType, content: string, index: number): IChatMessage {
    return {
      id,
      author: this.translateService.instant(authorKey),
      authorType,
      showAuthorInfo: false,
      content,
      dateTime: new Date().toISOString(),
      index,
    };
  }

  private getRouteTemplate(route: ActivatedRoute): string {
    const segments = route.snapshot.pathFromRoot.flatMap((r) => r.routeConfig?.path?.split('/') ?? []).filter(Boolean);

    return `/${segments.join('/')}`;
  }

  private mapToChatMessage(msg: ConversationMessageModel, index: number): CompanionChatMessage {
    const isUser = msg.role === 'user' || msg.role === 'human';
    const base = isUser
      ? (this.mapHistoryUserMessage(msg, index) ?? this.createUserMessage(index, msg.content))
      : this.buildAgentMessage(index, msg.content, msg.toolCalls, msg.messageType, msg.requestId, msg.form, msg.error);
    return { ...base, id: msg.messageId, dateTime: msg.createdAt };
  }

  // The selected date format as the moment pattern formatDate renders (e.g. "MM/DD/YYYY, h:mm A").
  private getDateTimePattern(): string {
    const localeData = moment.localeData(this.store.selectSnapshot(LocalizationState.selectedDateFormat));
    return `${localeData.longDateFormat('L')}, ${localeData.longDateFormat('LT')}`;
  }
}
